// src/pages/AffectationsCreate.js - AVEC GESTION DES ERREURS D'ABONNEMENT
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const AffectationsCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const siteIdParam = queryParams.get('site_id');
  const agentIdParam = queryParams.get('agent_id');
  
  const [formData, setFormData] = useState({
    id_site: siteIdParam || "",
    id_agent: agentIdParam || "",
    periode: ""
  });
  
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
        
        if (String(normalizedData.profile).trim() !== "3") {
          setError("Permission refusée. Seuls les gestionnaires peuvent affecter des agents.");
          setLoading(false);
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchSites = async () => {
      if (!userInfo) return;
      
      setLoading(true);
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/sites/list.php?limit=1000&statut=1&supprimer=0`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data?.success) {
          setSites(response.data.data || []);
        } else {
          setError(response.data?.message || "Erreur de chargement des sites");
        }
      } catch (err) {
        setError("Erreur lors du chargement des sites");
      } finally {
        setLoading(false);
      }
    };
    
    if (userInfo) {
      fetchSites();
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!userInfo) return;
      
      setLoadingAgents(true);
      const token = localStorage.getItem("access_token");
      if (!token) return;
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/agents/list.php?status=1&affecte=0&show_deleted=0`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data?.success) {
          const agentsData = response.data.agents || [];
          
          const agentsWithUserId = await Promise.all(
            agentsData.map(async (agent) => {
              try {
                const detailsResponse = await axios.get(
                  `${API_BASE_URL}/agents/get.php?id=${agent.id_agent}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
                
                if (detailsResponse.data?.success) {
                  if (detailsResponse.data.agent?.id_user) {
                    return {
                      ...agent,
                      id_user: detailsResponse.data.agent.id_user
                    };
                  } else {
                    try {
                      const usersResponse = await axios.get(
                        `${API_BASE_URL}/users/list.php?profile=4&matricule=${agent.matricule_agent}`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                          },
                        }
                      );
                      
                      if (usersResponse.data?.success && usersResponse.data.data?.length > 0) {
                        const matchingUser = usersResponse.data.data.find(user => 
                          user.matricule_user === agent.matricule_agent
                        );
                        if (matchingUser) {
                          return {
                            ...agent,
                            id_user: matchingUser.id_user
                          };
                        }
                      }
                    } catch (userErr) {
                      console.warn("Erreur recherche utilisateur:", userErr);
                    }
                    
                    return agent;
                  }
                } else {
                  return agent;
                }
              } catch (detailsErr) {
                console.warn(`Erreur détails agent ${agent.id_agent}:`, detailsErr.message);
                return agent;
              }
            })
          );
          
          setAgents(agentsWithUserId);
        } else {
          setAgents([]);
        }
      } catch (err) {
        setAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    };
    
    if (userInfo) {
      fetchAgents();
    }
  }, [userInfo]);

  useEffect(() => {
    if (formData.id_site) {
      const site = sites.find(s => s.id_site.toString() === formData.id_site);
      setSelectedSite(site || null);
    } else {
      setSelectedSite(null);
    }
    
    if (formData.id_agent) {
      const agent = agents.find(a => a.id_agent.toString() === formData.id_agent);
      setSelectedAgent(agent || null);
    } else {
      setSelectedAgent(null);
    }
  }, [formData.id_site, formData.id_agent, sites, agents]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.id_site) {
      errors.push("Veuillez sélectionner un site");
    }
    
    if (!formData.id_agent) {
      errors.push("Veuillez sélectionner un agent");
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      const response = await axios.post(
        `${API_BASE_URL}/affectations/create.php`,
        {
          id_site: parseInt(formData.id_site),
          id_agent: parseInt(formData.id_agent),
          periode: formData.periode || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Affectation créée avec succès !");
        
        setTimeout(() => {
           navigate("/affectations");
        }, 2000);
      } else {
        setError(response.data?.message || "Erreur lors de la création de l'affectation");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        // 🔍 Vérifier si c'est une erreur d'abonnement
        if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setError("Permission refusée. Seuls les gestionnaires peuvent affecter des agents.");
        }
      } else if (err.response?.status === 404) {
        setError(err.response.data?.message || "Site ou agent non trouvé dans votre société");
      } else if (err.response?.status === 409) {
        setError(err.response.data?.message || "Cet agent est déjà affecté à ce site");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/affectations");
  };

  const handleReset = () => {
    setFormData({
      id_site: siteIdParam || "",
      id_agent: agentIdParam || ""
    });
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
  };

  if (!loading && userInfo && String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Nouvelle affectation</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/affectations">Affectations</Link>
              </li>
              <li className="breadcrumb-item active">Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ffebee',
                  color: COLORS.danger
                }}>
                  <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
                </div>
                <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Permission refusée</h5>
                <p style={{ color: COLORS.darkText }}>
                  Seuls les gestionnaires peuvent créer des affectations.
                  <br />
                  Contactez votre administrateur pour plus d'informations.
                </p>
              </div>
              <button
                className="btn fw-semibold"
                onClick={() => navigate("/affectations")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour aux affectations
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (loading || loadingAgents) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Nouvelle affectation</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/affectations">Affectations</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des données...</p>
              {userInfo && (
                <div className="mt-2 small" style={{ color: '#757575' }}>
                  Société ID: <strong>{userInfo.societe_id}</strong> • 
                  {loadingAgents ? " Chargement des agents..." : " Agents chargés"}
                </div>
              )}
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Nouvelle affectation</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/affectations">Affectations</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Création</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-link-45deg me-2" style={{ color: COLORS.secondary }}></i>
              Affecter un agent à un site
            </h5>

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">
                    {successMsg}
                    <div className="mt-2">
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#2e7d32' }}>
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Redirection en cours...
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                    <ul className="mb-0 mt-2">
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Sélection du site */}
                <div className="col-md-6">
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-building"></i>
                        </div>
                        Sélection du site
                      </h6>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-building me-1" style={{ color: COLORS.secondary }}></i>
                          Site <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-building"></i>
                          </span>
                          <select
                            className="form-select"
                            name="id_site"
                            value={formData.id_site}
                            onChange={handleChange}
                            required
                            disabled={saving || sites.length === 0}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Sélectionner un site</option>
                            {sites.map(site => (
                              <option key={site.id_site} value={site.id_site}>
                                {site.nom_site}
                                {site.statut_site !== 1 && " (Inactif)"}
                                {site.id_client && ` - Client: ${site.nom_client}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          {sites.length === 0 
                            ? "Aucun site disponible pour votre société. Créez d'abord un site."
                            : `${sites.length} site(s) disponible(s)`}
                        </div>
                      </div>
                      
                      {/* Détails du site sélectionné */}
                      {selectedSite && (
                        <div className="alert mt-3" style={{ 
                          backgroundColor: '#E3F2FD',
                          borderColor: COLORS.accent,
                          color: COLORS.primary,
                          borderRadius: '8px'
                        }}>
                          <div className="d-flex align-items-start">
                            <i className="bi bi-info-circle me-2 mt-1" style={{ color: COLORS.secondary }}></i>
                            <div>
                              <strong>Site sélectionné :</strong>
                              <div className="mt-2">
                                <table className="table table-sm table-borderless mb-0">
                                  <tbody>
                                    <tr>
                                      <td width="40%" style={{ fontWeight: '500', color: COLORS.darkText }}>Nom :</td>
                                      <td style={{ color: COLORS.darkText }}>{selectedSite.nom_site}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Client :</td>
                                      <td style={{ color: COLORS.darkText }}>{selectedSite.nom_client || "-"}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Adresse :</td>
                                      <td style={{ color: COLORS.darkText }}>{selectedSite.addresse_site || "-"}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Statut :</td>
                                      <td>
                                        <span className="badge" style={{ 
                                          backgroundColor: selectedSite.statut_site === 1 ? '#e8f5e9' : '#f5f5f5',
                                          color: selectedSite.statut_site === 1 ? '#2e7d32' : '#757575',
                                          padding: '4px 12px',
                                          borderRadius: '6px',
                                          fontWeight: '500'
                                        }}>
                                          {selectedSite.statut_site === 1 ? 'Actif' : 'Inactif'}
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-3">
                                <Link 
                                  to={`/sites/details/${selectedSite.id_site}`}
                                  className="btn btn-sm fw-semibold"
                                  target="_blank"
                                  style={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    backgroundColor: 'transparent',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '0.85rem',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = COLORS.primary;
                                    e.target.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = COLORS.primary;
                                  }}
                                >
                                  <i className="bi bi-eye me-1"></i>
                                  Voir les détails du site
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sélection de l'agent */}
                <div className="col-md-6">
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-person-badge"></i>
                        </div>
                        Sélection de l'agent
                      </h6>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-person me-1" style={{ color: COLORS.secondary }}></i>
                          Agent <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person-badge"></i>
                          </span>
                          <select
                            className="form-select"
                            name="id_agent"
                            value={formData.id_agent}
                            onChange={handleChange}
                            required
                            disabled={saving || agents.length === 0}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Sélectionner un agent</option>
                            {agents.map(agent => (
                              <option 
                                key={agent.id_agent} 
                                value={agent.id_agent}
                              >
                                {agent.prenom_agent} {agent.nom_agent}
                                {agent.matricule_agent && ` (${agent.matricule_agent})`}
                                {agent.status_agent !== 1 && " (Inactif)"}
                                {!agent.id_user && " [Pas de lien user]"}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          {agents.length === 0 
                            ? `Aucun agent disponible dans votre société. Créez d'abord un agent.`
                            : `${agents.length} agent(s) disponible(s) - Seuls les agents non affectés sont affichés`}
                          <br />
                          <small className="mt-1 d-block" style={{ color: '#9e9e9e' }}>
                            {agents.filter(a => a.id_user).length} agent(s) avec lien utilisateur
                          </small>
                        </div>
                      </div>

                      {/* Période : journée ou soirée */}
                      <div className="mb-4">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-clock me-2" style={{ color: COLORS.secondary }}></i>
                          Période
                        </label>
                        <select
                          className="form-select"
                          name="periode"
                          value={formData.periode}
                          onChange={handleChange}
                          disabled={saving}
                          style={{ borderColor: '#e0e0e0', borderRadius: '8px', color: COLORS.darkText }}
                        >
                          <option value="">Non précisée</option>
                          <option value="jour">Journée</option>
                          <option value="soir">Soirée</option>
                        </select>
                      </div>

                      {/* Détails de l'agent sélectionné */}
                      {selectedAgent && (
                        <div className="alert mt-3" style={{ 
                          backgroundColor: '#E3F2FD',
                          borderColor: COLORS.accent,
                          color: COLORS.primary,
                          borderRadius: '8px'
                        }}>
                          <div className="d-flex align-items-start">
                            <i className="bi bi-info-circle me-2 mt-1" style={{ color: COLORS.secondary }}></i>
                            <div>
                              <strong>Agent sélectionné :</strong>
                              <div className="mt-2">
                                <table className="table table-sm table-borderless mb-0">
                                  <tbody>
                                    <tr>
                                      <td width="40%" style={{ fontWeight: '500', color: COLORS.darkText }}>Nom :</td>
                                      <td style={{ color: COLORS.darkText }}>{selectedAgent.prenom_agent} {selectedAgent.nom_agent}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Matricule :</td>
                                      <td style={{ color: COLORS.darkText }}>{selectedAgent.matricule_agent || "-"}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Téléphone :</td>
                                      <td style={{ color: COLORS.darkText }}>{selectedAgent.tel_agent || "-"}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Statut :</td>
                                      <td>
                                        <span className="badge" style={{ 
                                          backgroundColor: selectedAgent.status_agent === 1 ? '#e8f5e9' : '#f5f5f5',
                                          color: selectedAgent.status_agent === 1 ? '#2e7d32' : '#757575',
                                          padding: '4px 12px',
                                          borderRadius: '6px',
                                          fontWeight: '500'
                                        }}>
                                          {selectedAgent.status_agent === 1 ? 'Actif' : 'Inactif'}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>Affectation :</td>
                                      <td>
                                        <span className="badge" style={{ 
                                          backgroundColor: '#e8f5e9',
                                          color: '#2e7d32',
                                          padding: '4px 12px',
                                          borderRadius: '6px',
                                          fontWeight: '500'
                                        }}>
                                          Disponible
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ fontWeight: '500', color: COLORS.darkText }}>ID User :</td>
                                      <td>
                                        {selectedAgent.id_user ? (
                                          <span className="badge" style={{ 
                                            backgroundColor: COLORS.accent,
                                            color: COLORS.primary,
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontWeight: '500'
                                          }}>
                                            {selectedAgent.id_user}
                                          </span>
                                        ) : (
                                          <span className="badge" style={{ 
                                            backgroundColor: '#fff8e1',
                                            color: '#ff8f00',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontWeight: '500'
                                          }}>
                                            Non lié
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-3">
                                {selectedAgent.id_user ? (
                                  <Link 
                                    to={`/users/details/${selectedAgent.id_user}`}
                                    className="btn btn-sm fw-semibold"
                                    target="_blank"
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: COLORS.primary,
                                      backgroundColor: 'transparent',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = COLORS.primary;
                                      e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = COLORS.primary;
                                    }}
                                  >
                                    <i className="bi bi-eye me-1"></i>
                                    Voir les détails de l'agent
                                  </Link>
                                ) : (
                                  <button
                                    className="btn btn-sm fw-semibold"
                                    onClick={() => {
                                      alert("Cet agent n'est pas lié à un utilisateur. Il a peut-être été créé via l'ancien système.");
                                    }}
                                    style={{
                                      color: '#ff9800',
                                      borderColor: '#ff9800',
                                      backgroundColor: 'transparent',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '0.85rem',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#ff9800';
                                      e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = '#ff9800';
                                    }}
                                  >
                                    <i className="bi bi-eye me-1"></i>
                                    Agent non lié à un utilisateur
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Récapitulatif */}
              {selectedSite && selectedAgent && (
                <div className="card mb-4" style={{ 
                  borderRadius: '8px',
                  border: `2px solid ${COLORS.accent}`,
                  backgroundColor: '#E3F2FD'
                }}>
                  <div className="card-body">
                    <h6 className="card-title mb-4" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600'
                    }}>
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                        width: '30px',
                        height: '30px',
                        backgroundColor: COLORS.primary,
                        color: 'white'
                      }}>
                        <i className="bi bi-list-check"></i>
                      </div>
                      Récapitulatif de l'affectation
                    </h6>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card mb-3" style={{ 
                          backgroundColor: '#e8f5e9',
                          borderColor: COLORS.success,
                          borderRadius: '8px',
                          boxShadow: 'none'
                        }}>
                          <div className="card-body">
                            <h6 className="mb-3 d-flex align-items-center" style={{ color: '#2e7d32', fontWeight: '600' }}>
                              <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#2e7d32',
                                color: 'white',
                                fontSize: '0.75rem'
                              }}>
                                <i className="bi bi-building"></i>
                              </div>
                              Site
                            </h6>
                            <div className="d-flex align-items-center mb-2">
                              <div className="flex-grow-1">
                                <strong style={{ color: COLORS.darkText, display: 'block' }}>{selectedSite.nom_site}</strong>
                                <div style={{ color: '#757575', fontSize: '0.85rem' }}>
                                  {selectedSite.addresse_site || "Pas d'adresse"}
                                </div>
                              </div>
                              <span className="badge" style={{ 
                                backgroundColor: selectedSite.statut_site === 1 ? '#2e7d32' : '#757575',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontWeight: '500'
                              }}>
                                {selectedSite.statut_site === 1 ? 'Actif' : 'Inactif'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="card mb-3" style={{ 
                          backgroundColor: '#E3F2FD',
                          borderColor: COLORS.accent,
                          borderRadius: '8px',
                          boxShadow: 'none'
                        }}>
                          <div className="card-body">
                            <h6 className="mb-3 d-flex align-items-center" style={{ color: COLORS.primary, fontWeight: '600' }}>
                              <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                                width: '24px',
                                height: '24px',
                                backgroundColor: COLORS.primary,
                                color: 'white',
                                fontSize: '0.75rem'
                              }}>
                                <i className="bi bi-person"></i>
                              </div>
                              Agent
                            </h6>
                            <div className="d-flex align-items-center mb-2">
                              <div className="flex-grow-1">
                                <strong style={{ color: COLORS.darkText, display: 'block' }}>{selectedAgent.prenom_agent} {selectedAgent.nom_agent}</strong>
                                <div style={{ color: '#757575', fontSize: '0.85rem' }}>
                                  {selectedAgent.matricule_agent ? `Matricule: ${selectedAgent.matricule_agent}` : "Pas de matricule"}
                                </div>
                              </div>
                              <span className="badge" style={{ 
                                backgroundColor: selectedAgent.status_agent === 1 ? '#2e7d32' : '#757575',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontWeight: '500'
                              }}>
                                {selectedAgent.status_agent === 1 ? 'Actif' : 'Inactif'}
                              </span>
                            </div>
                            {!selectedAgent.id_user && (
                              <div className="mt-2 p-2 d-flex align-items-center" style={{ 
                                backgroundColor: '#fff8e1',
                                borderLeft: '3px solid #ffc107',
                                borderRadius: '4px'
                              }}>
                                <i className="bi bi-exclamation-triangle me-1" style={{ color: '#ff9800' }}></i>
                                <span style={{ color: '#ff8f00', fontSize: '0.85rem' }}>
                                  Cet agent n'est pas lié à un utilisateur
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="alert mt-3" style={{ 
                      backgroundColor: '#fff8e1',
                      borderColor: COLORS.warning,
                      color: '#ff8f00',
                      borderRadius: '8px',
                      borderLeft: '4px solid ' + COLORS.warning
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle me-2" style={{ color: '#ff9800' }}></i>
                        <div>
                          <strong>Information :</strong> Après cette affectation, l'agent sera marqué comme "affecté" 
                          et ne pourra plus être affecté à un autre site sans d'abord être retiré de celui-ci.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ 
                borderColor: COLORS.lightBg
              }}>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={saving}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleReset}
                    disabled={saving}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={saving || !formData.id_site || !formData.id_agent}
                  style={{
                    color: 'white',
                    backgroundColor: saving || !formData.id_site || !formData.id_agent ? '#B0BEC5' : COLORS.primary,
                    borderColor: saving || !formData.id_site || !formData.id_agent ? '#B0BEC5' : COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    opacity: (!formData.id_site || !formData.id_agent) ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!saving && formData.id_site && formData.id_agent) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving && formData.id_site && formData.id_agent) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Créer l'affectation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Section aide */}
        <div className="card mt-4" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none',
          backgroundColor: '#E3F2FD'
        }}>
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-2 text-center">
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center" style={{ 
                  width: '60px',
                  height: '60px',
                  backgroundColor: COLORS.primary,
                  color: 'white'
                }}>
                  <i className="bi bi-question-circle fs-4"></i>
                </div>
              </div>
              <div className="col-md-10">
                <h6 style={{ color: COLORS.primary, marginBottom: '8px', fontWeight: '600' }}>Besoin d'aide ?</h6>
                <p style={{ color: COLORS.darkText, fontSize: '0.95rem', marginBottom: '0' }}>
                  Les agents doivent être créés avant de pouvoir être affectés à des sites. 
                  Assurez-vous que l'agent et le site sont actifs avant de procéder à l'affectation.
                  <br />
                  <small className="text-muted mt-1 d-block">
                    <i className="bi bi-lightbulb me-1"></i>
                    Pour créer un nouvel agent, accédez à la page <Link to="/users/create" className="fw-semibold" style={{ color: COLORS.secondary }}>Création d'utilisateur</Link>.
                  </small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-6 {
            margin-bottom: 1.5rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AffectationsCreate;